import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LeadTypePill, { COLOR_CLASSES } from "@/components/LeadTypePill";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const COLORS = ["blue", "amber", "orange", "red", "violet", "emerald", "slate", "teal"];

export default function LeadTypes() {
    const [types, setTypes] = useState([]);
    const [name, setName] = useState("");
    const [color, setColor] = useState("blue");
    const [editing, setEditing] = useState(null);
    const [editName, setEditName] = useState("");
    const [editColor, setEditColor] = useState("blue");

    const load = async () => {
        const { data } = await api.get("/lead-types");
        setTypes(data);
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!name.trim()) return;
        try {
            await api.post("/lead-types", { name: name.trim(), color });
            setName("");
            setColor("blue");
            await load();
            toast.success("Lead type created");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const save = async () => {
        try {
            await api.put(`/lead-types/${editing.id}`, { name: editName, color: editColor });
            setEditing(null);
            await load();
            toast.success("Lead type updated");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this lead type? Leads will be unclassified.")) return;
        await api.delete(`/lead-types/${id}`);
        await load();
        toast.success("Lead type deleted");
    };

    return (
        <div className="space-y-6" data-testid="lead-types-page">
            <Link to="/leads" className="inline-flex items-center text-sm text-muted-foreground hover:text-gray-900">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to leads
            </Link>
            <div>
                <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Lead Types</h1>
                <p className="text-sm text-muted-foreground mt-1">Categorize leads. Used for campaign targeting and filtering.</p>
            </div>

            <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
                <h2 className="text-base font-medium text-gray-900 mb-3">Create new</h2>
                <div className="flex flex-col md:flex-row gap-3 md:items-end">
                    <div className="flex-1">
                        <Label htmlFor="lt-name">Name</Label>
                        <Input id="lt-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. NSF90" data-testid="new-lead-type-name" />
                    </div>
                    <div>
                        <Label>Color</Label>
                        <div className="flex gap-1.5 mt-2">
                            {COLORS.map(c => (
                                <button
                                    key={c}
                                    onClick={() => setColor(c)}
                                    className={`h-7 w-7 rounded-md ring-1 ring-inset transition-all ${COLOR_CLASSES[c]} ${color === c ? "ring-2 ring-offset-2 ring-primary" : ""}`}
                                    data-testid={`color-${c}`}
                                    aria-label={c}
                                />
                            ))}
                        </div>
                    </div>
                    <Button onClick={create} data-testid="create-lead-type-button">
                        <Plus className="h-4 w-4 mr-1" /> Create
                    </Button>
                </div>
            </div>

            <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50/70 text-left">
                        <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                            <th className="px-4 py-3 font-medium">Name</th>
                            <th className="px-4 py-3 font-medium">Color</th>
                            <th className="px-4 py-3 font-medium">Leads</th>
                            <th className="px-4 py-3 font-medium w-32"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {types.map(t => (
                            <tr key={t.id} className="border-t border-border" data-testid={`lead-type-row-${t.id}`}>
                                <td className="px-4 py-3"><LeadTypePill leadType={t} /></td>
                                <td className="px-4 py-3 text-gray-700 capitalize">{t.color}</td>
                                <td className="px-4 py-3 text-gray-700 tabular">{t.lead_count}</td>
                                <td className="px-4 py-3">
                                    <div className="flex justify-end gap-2">
                                        <Dialog open={editing?.id === t.id} onOpenChange={(o) => { if (!o) setEditing(null); }}>
                                            <DialogTrigger asChild>
                                                <Button variant="outline" size="sm" onClick={() => { setEditing(t); setEditName(t.name); setEditColor(t.color); }} data-testid={`edit-lead-type-${t.id}`}>Edit</Button>
                                            </DialogTrigger>
                                            <DialogContent>
                                                <DialogHeader><DialogTitle>Edit lead type</DialogTitle></DialogHeader>
                                                <div className="space-y-3">
                                                    <div>
                                                        <Label>Name</Label>
                                                        <Input value={editName} onChange={e => setEditName(e.target.value)} />
                                                    </div>
                                                    <div>
                                                        <Label>Color</Label>
                                                        <div className="flex gap-1.5 mt-2">
                                                            {COLORS.map(c => (
                                                                <button key={c} onClick={() => setEditColor(c)} className={`h-7 w-7 rounded-md ring-1 ring-inset ${COLOR_CLASSES[c]} ${editColor === c ? "ring-2 ring-offset-2 ring-primary" : ""}`} aria-label={c} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                <DialogFooter>
                                                    <Button onClick={save} data-testid="save-lead-type-button">Save</Button>
                                                </DialogFooter>
                                            </DialogContent>
                                        </Dialog>
                                        <Button variant="ghost" size="icon" onClick={() => remove(t.id)} data-testid={`delete-lead-type-${t.id}`}>
                                            <Trash2 className="h-4 w-4 text-rose-500" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
